/**
 * The editor's unsaved buffer, reachable from a Check.
 *
 * Check grades the workspace on disk, and the editor holds its buffer in the
 * page until Ctrl+S. So a learner who fixed the file on screen and pressed
 * Check without saving was graded on the version before the fix, and read the
 * same failure back as proof that the fix did not work — the most confusing
 * answer the product could give, to the learner doing everything right but
 * one keystroke. Every editor they will use at work saves before it runs
 * anything; this is how a Check does too.
 *
 * A registry keyed on the session rather than a prop threaded down, for the
 * reason `registerTerminalReader` is one (lib/terminal-tail.ts): the thing
 * that grades and the thing that holds the buffer are in different branches of
 * the tree, and neither should have to know how the other is laid out.
 *
 * What a flush does is the editor's business (EditorPane, `flushForCheck`),
 * including when it declines to write.
 */

type Flush = () => Promise<void>;

const flushers = new Map<string, Flush>();

/**
 * Say how to save the editor attached to this session.
 *
 * The returned function unregisters, and only if this is still the flush on
 * record: strict mode builds the pane twice, and the first one's teardown
 * must not take away the second one's registration.
 */
export function registerEditorFlush(sessionId: string, flush: Flush): () => void {
  flushers.set(sessionId, flush);
  return () => {
    if (flushers.get(sessionId) === flush) flushers.delete(sessionId);
  };
}

/**
 * Write the open buffer to disk before the workspace is graded. Resolves when
 * there is nothing left to write, or when writing was refused, and never
 * throws: a save that fails says so on the editor's own bar, and the Check
 * goes ahead on what is on disk rather than not at all.
 */
export async function flushEditor(sessionId: string): Promise<void> {
  const flush = flushers.get(sessionId);
  if (!flush) return;
  try {
    await flush();
  } catch {
    /* the editor has already said why */
  }
}
